import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PortableText } from '@portabletext/react';
import { client, urlFor } from '../lib/sanity';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const POST_QUERY = `*[_type == "post" && slug.current == $slug][0] {
  _id, title, slug, category, excerpt, publishedAt, body, mainImage
}`;

const portableTextComponents = {
  types: {
    image: ({ value }) => (
      <img
        src={urlFor(value).width(900).url()}
        alt={value.alt || ''}
        style={{
          width: '100%',
          borderRadius: '16px',
          margin: '32px 0',
        }}
      />
    ),
  },
  block: {
    h2: ({ children }) => (
      <h2 style={{
        color: '#ffffff',
        fontFamily: "'Climate Crisis', sans-serif",
        fontSize: '28px',
        fontWeight: '400',
        margin: '48px 0 16px',
        lineHeight: '1.2',
      }}>
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 style={{
        color: '#ffffff',
        fontFamily: "'Figtree', sans-serif",
        fontSize: '20px',
        fontWeight: '700',
        margin: '36px 0 12px',
      }}>
        {children}
      </h3>
    ),
    blockquote: ({ children }) => (
      <blockquote style={{
        borderLeft: '3px solid #C8102E',
        paddingLeft: '20px',
        margin: '32px 0',
        color: 'rgba(255,255,255,0.7)',
        fontStyle: 'italic',
        fontSize: '18px',
        lineHeight: '1.8',
      }}>
        {children}
      </blockquote>
    ),
    normal: ({ children }) => (
      <p style={{
        color: 'rgba(255,255,255,0.7)',
        fontFamily: "'Figtree', sans-serif",
        fontSize: '17px',
        lineHeight: '1.85',
        margin: '0 0 20px',
      }}>
        {children}
      </p>
    ),
  },
  marks: {
    strong: ({ children }) => <strong style={{ color: '#ffffff', fontWeight: '600' }}>{children}</strong>,
    link: ({ value, children }) => (
      <a
        href={value?.href}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#C8102E', textDecoration: 'underline' }}
      >
        {children}
      </a>
    ),
  },
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!client) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    client
      .fetch(POST_QUERY, { slug })
      .then((data) => {
        if (!data) setNotFound(true);
        else setPost(data);
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [slug]);

  const scrollToContact = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.querySelector('#contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: '#000', minHeight: '100vh' }}>
        <Navbar onContactClick={scrollToContact} />
        <div style={{ paddingTop: '160px', textAlign: 'center' }}>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Figtree', sans-serif" }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={{ backgroundColor: '#000', minHeight: '100vh' }}>
        <Navbar onContactClick={scrollToContact} />
        <div style={{ paddingTop: '160px', textAlign: 'center', padding: '160px 40px' }}>
          <h1 style={{
            color: '#ffffff',
            fontFamily: "'Climate Crisis', sans-serif",
            fontSize: '36px',
            fontWeight: '400',
            marginBottom: '16px',
          }}>
            Post Not Found
          </h1>
          <Link
            to="/blog"
            style={{
              color: '#C8102E',
              fontFamily: "'Figtree', sans-serif",
              fontSize: '15px',
              textDecoration: 'underline',
            }}
          >
            Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const formattedDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <div style={{ backgroundColor: '#000', minHeight: '100vh' }}>
      <Navbar onContactClick={scrollToContact} />

      <article style={{ paddingTop: '140px', paddingBottom: '120px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto', padding: '0 40px' }}>
          {/* Back link */}
          <Link
            to="/blog"
            style={{
              color: 'rgba(255,255,255,0.4)',
              fontFamily: "'Figtree', sans-serif",
              fontSize: '13px',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '40px',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#fff')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M11 7H3M3 7L7 3M3 7L7 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Blog
          </Link>

          {/* Category + date */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            {post.category && (
              <span style={{
                background: 'rgba(200,16,46,0.12)',
                border: '1px solid rgba(200,16,46,0.4)',
                borderRadius: '20px',
                padding: '4px 12px',
                color: '#ff6b7a',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '11px',
                fontWeight: '700',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}>
                {post.category}
              </span>
            )}
            {formattedDate && (
              <span style={{
                color: 'rgba(255,255,255,0.3)',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '13px',
              }}>
                {formattedDate}
              </span>
            )}
          </div>

          {/* Title */}
          <h1 style={{
            color: '#ffffff',
            fontFamily: "'Climate Crisis', sans-serif",
            fontSize: 'clamp(28px, 5vw, 48px)',
            fontWeight: '400',
            lineHeight: '1.15',
            marginBottom: '32px',
          }}>
            {post.title}
          </h1>

          {/* Main image */}
          {post.mainImage && (
            <img
              src={urlFor(post.mainImage).width(1200).url()}
              alt={post.title}
              style={{
                width: '100%',
                borderRadius: '18px',
                marginBottom: '48px',
                maxHeight: '480px',
                objectFit: 'cover',
              }}
            />
          )}

          {/* Body */}
          {post.body && (
            <div>
              <PortableText value={post.body} components={portableTextComponents} />
            </div>
          )}

          {/* Bottom CTA */}
          <div style={{
            marginTop: '80px',
            padding: '40px',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '18px',
            textAlign: 'center',
          }}>
            <p style={{
              color: 'rgba(255,255,255,0.5)',
              fontFamily: "'Figtree', sans-serif",
              fontSize: '14px',
              marginBottom: '16px',
            }}>
              Want to work together on strategy, governance, or leadership?
            </p>
            <button
              onClick={scrollToContact}
              style={{
                background: '#C8102E',
                color: '#ffffff',
                border: 'none',
                borderRadius: '100px',
                padding: '14px 32px',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '14px',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#a80d25')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#C8102E')}
            >
              Book a Strategy Call
            </button>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
